/**
 * 语言切换器 - 极简形式（带下拉菜单）
 */

import { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from '../../useTranslation';
import { useLanguageNavigation } from '../../hooks/useLanguageNavigation';
import type { ActiveLanguage } from '../../types';
import type { SizeClasses } from './types';

interface MinimalProps {
  sizeClasses: SizeClasses;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function Minimal({ sizeClasses, size = 'md', className = '' }: MinimalProps) {
  const { language, availableLanguages, languageName } = useTranslation();
  const { changeLanguage } = useLanguageNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭下拉
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode: string) => {
    changeLanguage(langCode as ActiveLanguage);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 rounded-lg transition-colors 
          hover:bg-[var(--y-surface-secondary)] text-[var(--y-text-secondary)] hover:text-[var(--y-text-primary)]
          ${sizeClasses[size]}
        `}
        title={languageName}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current language: ${languageName}. Click to change.`}
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span className="uppercase font-medium">{language}</span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 py-1.5 rounded-xl bg-[var(--y-surface-primary)] border border-[var(--y-border)] shadow-2xl z-[100] min-w-[140px] overflow-hidden"
          role="listbox"
          style={{ transform: 'translateY(4px)' }}
        >
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`
                w-full flex items-center justify-between px-3 py-2
                transition-colors text-left text-sm
                ${language === lang.code
                  ? 'text-[var(--y-accent)] bg-[var(--y-accent-light)]'
                  : 'text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)]/50'
                }
              `}
              role="option"
              aria-selected={language === lang.code}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden="true">{lang.flag}</span>
                <span className="uppercase">{lang.code}</span>
              </span>
              {language === lang.code && (
                <Check className="w-3 h-3" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
